import React from 'react'
import { withRouter } from 'react-router-dom'

import { compose, defaultProps, withHandlers, withState } from 'recompose'
import styled from 'styled-components'

import alertBadgeImg from 'assets/icons/alert-badge.png'
import Modal from 'components/Modal'
import { Primary as PrimaryButton } from 'components/Modal/buttons'
import { Body, Title } from 'components/Modal/typography'

type Props = {
  isOpen: boolean
  onRequestClose: () => void
  errorDetails?: ErrorDetails
}

type HandlerProps = {
  handleClick: () => void
}

type MergedProps = Props & HandlerProps

type ErrorDetails = {
  cta: string
  handler: () => void
  header: string
  message: string
}

const enhance = compose<MergedProps, Props>(
  withRouter,
  defaultProps({
    errorDetails: {},
  }),
  withHandlers<Props, HandlerProps>({
    handleClick:
      ({ errorDetails, onRequestClose }) =>
      () => {
        if (errorDetails?.handler) {
          errorDetails?.handler()
        } else {
          onRequestClose()
        }
      },
  }),
)

const StyledModal = styled(Modal)`
  padding: 0;
`

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 20px;
`

const AlertImg = styled.img`
  height: 104px;
  margin: 50px 0 24px;
  width: 104px;
`

const ErrorModal: React.ComponentClass<Props> = enhance(
  ({ isOpen, errorDetails, onRequestClose }) => (
    <StyledModal
      closeOnEsc
      closeOnOutsideClick
      isOpen={isOpen}
      onRequestClose={onRequestClose}
    >
      {({ exit }: { exit: () => void }) => (
        <>
          <Container>
            <AlertImg src={alertBadgeImg} alt="alert badge" />
            <Title>Oops</Title>
            <Body>
              {errorDetails?.message ||
                'It looks like something went wrong. Please go back and try that again.'}
            </Body>
          </Container>
          <PrimaryButton onClick={exit} role="button" tabIndex={0}>
            {errorDetails?.cta || 'Close'}
          </PrimaryButton>
        </>
      )}
    </StyledModal>
  ),
)

export default ErrorModal

export type ErrorProps = {
  showError: boolean
  setShowError: (showError: boolean) => void
  errorDetails?: ErrorDetails
  setErrorDetails?: (errorDetails: ErrorDetails) => void
}

type ErrorHandlerProps = {
  handleCloseError?: () => void
}

export type MergedErrorProps = ErrorProps & ErrorHandlerProps

export const withErrorHandlers = compose<MergedErrorProps, MergedErrorProps>(
  withState('showError', 'setShowError', false),
  withState('errorDetails', 'setErrorDetails', {
    cta: null,
    handler: null,
    header: null,
    message: null,
  }),
  withHandlers<MergedErrorProps, ErrorHandlerProps>({
    handleCloseError:
      ({ setShowError }) =>
      () => {
        setShowError(false)
      },
  }),
)
